<script setup>
import WatchLayout from '@/Layouts/New/WatchLayout.vue'
import EpisodeList from '@/Components/New/Courses/EpisodeList.vue'
import Comments from '@/Components/New/Comments/Comments.vue'
import CourseProgress from '@/Components/New/Courses/CourseProgress.vue'
import CourseOverview from '@/Components/New/Courses/CourseOverview.vue'
import Player from '@/Components/New/Player/Player.vue'
import { Link, router, usePage, Head } from '@inertiajs/vue3'
import { computed, ref, onMounted, watch } from 'vue'
import usePlayerSettings from '@/Composables/usePlayerSettings'
import VueScrollTo from 'vue-scrollto'
import Modal from '@/Components/New/Modal.vue'
import { useProgressStore } from '@/Stores/progress'
import axios from 'axios'
import EpisodeCountdownModal from "../../Components/Watch/EpisodeCountdownModal.vue";
import { useScroll } from '@vueuse/core'
import UserMenuLarge from "../../Layouts/New/Components/UserMenuLarge.vue";
import ChevronSvg from "@svg/new-chevron.svg";
import GitHubSvg from '@svg/new-github.svg'
import DownloadSvg from '@svg/new-download.svg'
import AutoplaySvg from '@svg/new-autoplay.svg'
import MarkIncompleteSvg from '@svg/new-mark-incomplete.svg'
import MarkCompleteSvg from '@svg/new-mark-complete.svg'
import ArrowSvg from "@svg/new-arrow.svg";
import CircleCheckSvg from '@svg/new-circle-check.svg'
import EllipsisCircleSvg from '@svg/new-ellipsis-circle.svg'
import TranscriptSvg from '@svg/new-transcript.svg'
import AccessDenied from "../../Components/Watch/AccessDenied.vue";
import Transcript from "../../Components/Watch/Transcript.vue";
import EpisodeOverview from '../../Components/New/Courses/EpisodeOverview.vue'

const page = usePage()
const progressStore = useProgressStore()
const { state } = usePlayerSettings()
const currentPlayerSeconds = ref(0)
const player = ref(null)

let yFromTop = ref(null)

const props = defineProps({
    course: Object,
    chapters: Array,
    episodes: Array,
    currentEpisode: Object,
    transcript: Array
})

progressStore.setEpisodes(props.episodes.filter(episode => !episode.upcoming))
progressStore.setDuration(props.course.duration_seconds)

const scrollToEpisode = () => {
    VueScrollTo.scrollTo(`#episodes-episode-${props.currentEpisode.id}`, {
        container: '#episodes',
        offset: props.chapters.length ? -56 : 0,
        duration: 0
    })
}

onMounted(() => {
    const { y } = useScroll(document)
    watch(y, (y) => yFromTop.value = y)

    scrollToEpisode()
})

watch(() => props.currentEpisode.id, () => {
    scrollToEpisode()
})

const nextEpisode = computed(() =>
    props.episodes.filter(episode => !episode.upcoming)[
        props.episodes.findIndex(episode => episode.slug === props.currentEpisode.slug) + 1
    ]
)

const previousEpisode = computed(() =>
    props.episodes.filter(episode => !episode.upcoming)[
        props.episodes.findIndex(episode => episode.slug === props.currentEpisode.slug) - 1
    ]
)

const playerIsFullscreen = ref(false)
const showAutoplayModal = ref(false)
const showCourseCompletedModal = ref(false)

const redirectToNextEpisode = () => {
    router.get(`/watch/${props.course.slug}/${nextEpisode.value.slug}`, {}, {
        preserveScroll: true,
        preserveState: true,
        only: ['currentEpisode', 'transcript'],
    })
}

const cancelAutoplayModal = () => {
    showAutoplayModal.value = false
}

const handleEnded = () => {
    storeProgress(props.currentEpisode.duration_seconds)

    if (!state.value.autoplay) {
        return
    }

    if (!nextEpisode.value) {
        showCourseCompletedModal.value = true
        return
    }

    if (playerIsFullscreen.value === true) {
        redirectToNextEpisode()
        return
    }

    showAutoplayModal.value = true
}

const storeProgress = (seconds) => {
    if (!page.props.auth.user) {
        return
    }

    axios.post(`/courses/${props.course.slug}/episodes/${props.currentEpisode.slug}/progress`, {
        seconds: seconds
    })
}

const handleProgressInterval = (seconds) => {
    if (progressStore.episodeIsComplete(props.currentEpisode.id)) {
        return
    }

    storeProgress(seconds)
}

const updateProgressStore = (seconds) => {
    progressStore.$patch((state) => {
        state.episodes.find(episode => episode.id === props.currentEpisode.id).completed_seconds = seconds
    })
}

const handleProgress = (seconds) => {
    if (!page.props.auth.user) {
        return
    }

    if (progressStore.episodeIsComplete(props.currentEpisode.id)) {
        return
    }

    updateProgressStore(seconds)
}

const markCurrentEpisodeComplete = () => {
    handleProgress(props.currentEpisode.duration_seconds)
    storeProgress(props.currentEpisode.duration_seconds)
}

const markCurrentEpisodeIncomplete = () => {
    updateProgressStore(0)
    storeProgress(0)
}
</script>

<template>
    <Head :title="`${currentEpisode.title}: ${course.title}`">
        <meta name="description" :content="course.description_short" />
        <meta property="og:title" :content="`${currentEpisode.title}: ${course.title}`">
        <meta property="og:description" :content="course.description_short">
        <meta property="og:type" content="article">
        <meta property="og:image" :content="`${$page.props.config['app.url']}/courses/${course.slug}/ogimage`">
        <meta property="og:url" :content="`${$page.props.config['app.url']}/courses/${course.slug}`">
        <meta name="twitter:card" content="summary_large_image">
        <meta name="twitter:site" content="@teamcodecourse">
        <meta name="twitter:title" :content="course.title">

        <link rel="canonical" :href="`${$page.props.config['app.url']}${$page.url}`">
    </Head>

    <WatchLayout>
        <template v-slot:title>
            <Link :href="`/courses/${course.slug}`" class="text-[rgba(243,251,255,1)] py-2 font-medium flex text-sm md:text-base items-center space-x-2 group truncate text-ellipsis" :style="`opacity: ${Math.max(0, 100 - parseInt((yFromTop / 15) * 100))}%`">
                <ChevronSvg class="shrink-0 rotate-90 text-[rgba(112,120,176,1)] size-7 md:size-8 group-hover:-translate-x-1.5 transition-all duration-75" />
                <span class="truncate">{{ course.title }}</span>
            </Link>
        </template>

        <div class="3xl:max-w-[90%] mx-auto">
            <!-- Player -->
            <div class="bg-[rgba(20,22,57,1)] md:rounded-t-lg !pb-0 -ml-6 -mr-6 md:-ml-0 md:-mr-0 overflow-hidden blur-performance-fix">
                <div v-if="currentEpisode.vimeo_id">
                    <Player
                        ref="player"
                        :currentTime="currentEpisode.user.completed_seconds === props.currentEpisode.duration_seconds ? 0 : currentEpisode.user.completed_seconds"
                        :episode="currentEpisode"
                        v-on:ended="handleEnded"
                        v-on:progress="handleProgress($event); currentPlayerSeconds = $event"
                        v-on:progress-interval="handleProgressInterval"
                        v-on:fullscreen="playerIsFullscreen = $event"
                    />
                </div>
                <AccessDenied :course="course" v-else />
            </div>
            <!-- End player -->

            <!-- Player controls -->
            <div class="bg-[rgba(20,22,57,1)] px-6 py-3 pb-3.5 md:rounded-b-2xl flex flex-wrap md:flex-nowrap items-center justify-between -ml-6 -mr-6 md:-ml-0 md:-mr-0 md:space-x-3">
                <div class="truncate w-full">
                    <div class="text-[rgba(143,117,234,1)] font-medium text-xs leading-none">Playing</div>
                    <div class="text-[rgba(233,232,255,1)] font-semibold text-sm mt-1.5 truncate text-ellipsis">
                        {{ currentEpisode.order.toString().length === 1 ? `0${currentEpisode.order}` : `${currentEpisode.order}` }}. {{ currentEpisode.title }}
                    </div>
                </div>
                <div class="flex items-center space-x-2.5 mt-4 md:mt-0 justify-between md:justify-end w-full">
                    <div class="flex items-center space-x-2.5">
                        <component :is="$page.props.auth.user?.subscription?.active ? `a` : Link" v-if="course.github_repository" :href="$page.props.auth.user?.subscription?.active ? course.github_repository : `/pro`" target="_blank" :preserve-scroll="$page.props.auth.user?.subscription?.active" :data="{ from: $page.url }" class="h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2.5 font-medium whitespace-nowrap" aria-label="Source code">
                            <GitHubSvg class="size-4 fill-[rgba(233,232,255,1)]" />
                            <span class="hidden md:block">Source code</span>
                        </component>

                        <a class="h-10 max-h-10 px-3 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2 font-medium" :href="$page.props.auth.user?.subscription.active ? `/courses/${course.slug}/episodes/${currentEpisode.slug}/download` : `/pro`" rel="nofollow" v-if="currentEpisode.has_download" aria-label="Download video">
                            <DownloadSvg class="size-5" />
                        </a>

                        <button v-on:click="state.autoplay = !state.autoplay" class="h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2 font-medium" :class="{ 'bg-gradient-to-r from-[rgba(90,74,210,1)] to-[rgba(121,126,243,1)]': state.autoplay }" aria-label="Toggle autoplay">
                            <AutoplaySvg class="size-5" />
                            <span class="hidden md:block">Autoplay</span>
                        </button>

                        <button v-if="transcript" v-on:click="state.transcript = !state.transcript" class="h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2 font-medium" :class="{ 'bg-gradient-to-r from-[rgba(90,74,210,1)] to-[rgba(121,126,243,1)]': state.transcript }" aria-label="Toggle autoplay">
                            <TranscriptSvg class="size-5" />
                            <span class="hidden md:block">Transcript</span>
                        </button>

                        <button v-if="$page.props.auth.user" v-on:click="progressStore.episodeIsComplete(currentEpisode.id) ? markCurrentEpisodeIncomplete() : markCurrentEpisodeComplete()" class="hidden md:flex h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2 font-medium" :aria-label="`Mark ${progressStore.percentageProgressForEpisode(currentEpisode.id) === 100 ? `incomplete` : `complete`}`">
                            <MarkCompleteSvg v-if="!progressStore.episodeIsComplete(currentEpisode.id)" class="size-5" />
                            <MarkIncompleteSvg v-if="progressStore.episodeIsComplete(currentEpisode.id)" class="size-5" />
                            <span class="hidden md:block whitespace-nowrap w-[168px] text-left">Mark episode {{ progressStore.episodeIsComplete(currentEpisode.id) ? `incomplete` : `complete` }}</span>
                        </button>
                    </div>
                    <div class="flex items-center space-x-2.5">
                        <component :is="previousEpisode ? Link : `div`" :href="`/watch/${course.slug}/${previousEpisode?.slug}`" preserve-scroll replace class="h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2.5 font-medium" aria-label="Previous episode">
                            <ArrowSvg class="size-5" :class="{ 'opacity-20': !previousEpisode }" />
                        </component>

                        <component :is="nextEpisode ? Link : `div`" :href="`/watch/${course.slug}/${nextEpisode?.slug}`" preserve-scroll replace class="h-10 max-h-10 px-3.5 bg-[rgba(35,40,77,1)] rounded-lg flex items-center justify-center text-[rgba(233,232,255,1)] text-xs space-x-2.5 font-medium" aria-label="Next episode">
                            <ArrowSvg class="size-5 rotate-[180deg]" :class="{ 'opacity-20': !nextEpisode }" />
                        </component>
                    </div>
                </div>
            </div>
            <!-- End player controls -->
        </div>

        <div class="space-y-8 lg:max-w-5xl xl:max-w-6xl mx-auto">
            <div class="xl:fixed right-0 xl:w-[360px] 2xl:w-[400px] top-22 self-start xl:h-[calc(100vh-theme('spacing.22'))]">
                <div class="h-full flex flex-col bg-[rgba(20,22,57,1)] rounded-2xl xl:rounded-none xl:rounded-tl-2xl relative after:absolute after:bg-contain after:bg-[url('/images/light-circle.svg')] after:-right-[300px] after:-top-[300px] after:w-[700px] after:h-[700px] after:z-[1] after:pointer-events-none overflow-hidden">
                    <div class="p-6 md:p-8">
                        <h2 class="text-xl font-semibold bg-gradient-to-r from-[rgba(137,128,245,1)] to-[rgba(255,200,255,1)] text-transparent bg-clip-text leading-none">
                            Episodes
                        </h2>

                        <CourseProgress
                            class="mt-6"
                            :totalSeconds="course.duration_seconds"
                            :percentageProgress="progressStore.percentageProgress"
                            :playedSeconds="progressStore.playedSeconds"
                            :remainingSeconds="progressStore.remainingSeconds"
                            :showUnauthenticated="!$page.props.auth.user"
                        />
                    </div>

                    <EpisodeList identifier="episodes" :chapters="chapters" :episodes="episodes" :course="course" :currentEpisode="currentEpisode" class="grow !max-h-none !z-[1]" />
                </div>
            </div>

            <!--Transcript-->
            <Transcript
                v-if="transcript && state.transcript"
                :transcript="transcript"
                :currentSeconds="currentPlayerSeconds"
                v-on:close="state.transcript = false"
                v-on:timeupdate="player?.setCurrentTime($event)"
            />
            <!--/Transcript-->

            <div>

            </div>

            <EpisodeOverview :episode="currentEpisode" v-if="currentEpisode.description_markdown" />

            <div class="bg-[rgba(20,22,57,1)] p-6 md:p-10 rounded-2xl">
                <h2 class="text-xl text-[rgba(243,251,2551)] font-semibold leading-none">
                    Episode discussion
                </h2>
                <Comments
                    :key="currentEpisode.id"
                    :index="`/courses/${course.slug}/episodes/${currentEpisode.slug}/comments`"
                    :store="`/courses/${course.slug}/episodes/${currentEpisode.slug}/comments`"
                />
            </div>
        </div>
    </WatchLayout>

    <EpisodeCountdownModal v-if="nextEpisode && showAutoplayModal" :nextEpisode="nextEpisode" v-on:ended="redirectToNextEpisode(); cancelAutoplayModal()" v-on:cancel="cancelAutoplayModal" />

    <Modal :show="showCourseCompletedModal" v-on:close="showCourseCompletedModal = false" class="bg-[rgba(20,22,57,1)] max-w-md">
        <div class="p-12">
            <EllipsisCircleSvg v-if="course.ongoing" class="size-20 mx-auto text-[rgba(25,140,255,1)] drop-shadow-[2px_2px_6px_rgba(25,140,255,0.5)]" />
            <CircleCheckSvg v-else class="size-20 mx-auto text-[rgba(25,140,255,1)] drop-shadow-[2px_2px_6px_rgba(25,140,255,0.5)]" />

            <div class="text-3xl font-semibold text-[rgba(233,232,255,1)] text-center mt-4 leading-none">
                {{ course.ongoing ? `More on the way` : `You're done` }}
            </div>

            <div class="mt-4 text-[rgba(169,198,249,1)] text-center">
                <template v-if="course.ongoing">
                    You've reached the end of the available episodes. Check back shortly for updates.
                </template>
                <template v-else>
                    You've finished <span class="text-[rgba(233,232,255,1)] font-medium text-center">{{ course.title }}</span>.
                </template>
            </div>

            <div class="flex items-center space-x-3 mt-12">
                <button v-on:click="showCourseCompletedModal = false" class="bg-[rgba(27,32,74,1)] rounded-full font-medium text-[rgba(148,162,209,1)] w-full h-12 leading-none text-sm">
                    Close
                </button>
            </div>
        </div>
    </Modal>
</template>
